import { useContext } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import useQuery from 'hooks/useQuery';
import { getNestedCategory } from 'domain/categories/helpers';
import { workspaceDoctypesSelector, workspaceSubtypesSelector } from 'domain/documents';
import { documentWorkSpaceTypeSelector } from 'domain/companies/companiesSelector';
import { useDocumentsAggregations } from 'domain/documents/queries';
import type { SearchDoctype, SearchSubtypeChip } from 'domain/documents/schemas';
import CompanyWorkspaceContext from 'pages/company/WorkspaceRoute/CompanyWorkspaceContext';

// Route-derived inputs for documents/aggregations — mirrors the grid-search global filter
// (useGridContext) minus subtags/filterModel: companyId, active category, confidential flag, and
// the URL global filter (date/text/query) forwarded verbatim.
const useWorkspaceAggregationsParams = () => {
  const params = useParams();
  const query = useQuery();
  const { companyType } = useContext(CompanyWorkspaceContext);
  return {
    companyId: params.companyId ?? '',
    category: getNestedCategory(params) ?? '',
    isConfidential: companyType === 'confidential',
    query,
  };
};

// Source-agnostic read of the workspace doctype tabs. Tile reads Redux (aggregations ride the
// saga-owned search_new poll, kept fresh by the worker); grid reads TanStack (documents/aggregations,
// its own endpoint — grid has no worker). No TanStack→Redux sync; the grid query is enabled only in
// grid view, so tile makes no extra request.
export const useWorkspaceDoctypes = (): SearchDoctype[] => {
  const isGrid = useSelector(documentWorkSpaceTypeSelector) === 'grid';
  const tileTabs = useSelector(workspaceDoctypesSelector);
  const params = useWorkspaceAggregationsParams();
  const { tabs } = useDocumentsAggregations(params, isGrid);
  return isGrid ? tabs : tileTabs;
};

// Same source split for the Inbox sub-type chips. Both accessors call useDocumentsAggregations with
// the same params → same query key → TanStack dedupes to a single fetch.
export const useWorkspaceSubtypes = (): SearchSubtypeChip[] => {
  const isGrid = useSelector(documentWorkSpaceTypeSelector) === 'grid';
  const tileSubtypes = useSelector(workspaceSubtypesSelector);
  const params = useWorkspaceAggregationsParams();
  const { subtypes } = useDocumentsAggregations(params, isGrid);
  return isGrid ? subtypes : tileSubtypes;
};
